/*
 * Overview: the partner's commission (rate, this month, this FY, lifetime),
 * the wallet at a glance, the referral funnel, clients with their share of the
 * commission, twelve months of earnings and the latest commissions.
 *
 * Three calls. GET /dashboard carries everything except the per-client split,
 * which comes from GET /clients; if that one fails, only the Clients card says
 * so. The Profit share card loads GET /profit-share itself (ProfitShareCard)
 * and fails on its own. The commission figures are worked out here from `earnings_by_month`
 * (accrued only, by IST month) and `wallet.lifetime`, so they leave out
 * reversals and include commission still on hold, the same as the chart.
 */

import { useState } from "react";
import { clients, partner, type ClientSummary, type Commission, type Dashboard, type PartnerMe } from "../api/client";
import { useLoad, type Load } from "../api/useLoad";
import { useMe } from "../auth/AuthContext";
import { COMMISSION_STATE, EmptyState, LoadError, SkeletonRows, Status } from "./bits";
import CommissionSheet from "./CommissionSheet";
import EarningsChart from "./EarningsChart";
import ProfitShareCard from "./ProfitShareCard";
import ReferralLinkCard from "./ReferralLinkCard";
import TierCard from "./TierCard";
import {
  firstName,
  fmtDate,
  fmtMonth,
  fromPaise,
  greeting,
  inr,
  inrWhole,
  plural,
  rateLabel,
  toPaise,
  todayIst,
} from "./format";
import { ArrowRight, BarChart, Plus, Receipt, Wallet } from "./icons";
import type { View } from "./Shell";
import "./clients.css";
import "./overview.css";

export default function OverviewWorkspace({
  onNavigate,
  onRefer,
  contactEmail,
}: {
  onNavigate: (view: View) => void;
  onRefer: () => void;
  /** Shell's PARTNERS_EMAIL, passed down so the address stays defined in one place there. */
  contactEmail: string;
}) {
  const me = useMe();
  const dash = useLoad(() => partner.dashboard());
  const clientList = useLoad(() => clients.list());
  const d = dash.data;
  const [open, setOpen] = useState<Commission | null>(null);

  return (
    <>
      <section className="page-heading">
        <div>
          <p className="eyebrow">Partner overview</p>
          <h1>
            {greeting()}, {firstName(me.full_name)}.
          </h1>
          <p className="page-heading__sub">
            {d ? (
              <>
                You&apos;ve referred {plural(d.referrals.total, "person", "people")};{" "}
                <strong>
                  {d.clients.total} {d.clients.total === 1 ? "is a Wealthoak client" : "are Wealthoak clients"}
                </strong>
                .
              </>
            ) : dash.error ? (
              "Your overview could not be loaded."
            ) : (
              "Loading your overview…"
            )}
          </p>
        </div>
        <div className="page-actions">
          <button className="button button--secondary" onClick={() => onNavigate("wallet")}>
            <Wallet size={16} /> Wallet
          </button>
          <button className="button button--primary" onClick={onRefer}>
            <Plus size={16} /> Refer a client
          </button>
        </div>
      </section>

      {dash.error && !d ? (
        <LoadError message={dash.error} onRetry={dash.reload} />
      ) : (
        <>
          <CommissionSummary d={d} me={me} contactEmail={contactEmail} onNavigate={onNavigate} />

          <TierCard program={me.commission_program} inProgress={d?.wallet.in_progress} />

          <WalletTiles d={d} onNavigate={onNavigate} />

          <ProfitShareCard />

          <ReferralLinkCard onAddedUser={() => onNavigate("referrals")} />

          <section className="ov-grid">
            <article className="card card--pad ov-earnings" aria-labelledby="ov-earn-title">
              <div className="card-heading">
                <div>
                  <p className="eyebrow">Last 12 months</p>
                  <h2 id="ov-earn-title">Commission earned</h2>
                </div>
                {d && (
                  <div className="ov-earnings__total">
                    <strong className="num">
                      {inr(d.earnings_by_month.reduce((s, m) => s + (Number(m.amount) || 0), 0))}
                    </strong>
                  </div>
                )}
              </div>
              {d ? (
                d.earnings_by_month.some((m) => Number(m.amount) > 0) ? (
                  <EarningsChart months={d.earnings_by_month} />
                ) : (
                  <EmptyState icon={BarChart} title="No commission yet">
                    Your earnings chart fills in once a client you referred pays for their Wealthoak plan.
                  </EmptyState>
                )
              ) : (
                <div className="ov-chart-skel" role="status" aria-label="Loading chart">
                  {Array.from({ length: 12 }, (_, i) => (
                    <span key={i} className="skel" style={{ height: `${24 + ((i * 37) % 70)}%` }} />
                  ))}
                </div>
              )}
            </article>

            <div className="ov-side">
              <article className="card card--pad" aria-labelledby="ov-funnel-title">
                <div className="card-heading">
                  <div>
                    <p className="eyebrow">Referrals</p>
                    <h2 id="ov-funnel-title">{d ? plural(d.referrals.total, "referral") : "Referrals"}</h2>
                  </div>
                  <button className="text-button" onClick={() => onNavigate("referrals")}>
                    View all <ArrowRight size={14} />
                  </button>
                </div>
                {d ? <Funnel counts={d.referrals} /> : <SkeletonRows rows={4} label="Loading referrals" />}
              </article>

              <article className="card card--pad" aria-labelledby="ov-clients-title">
                <div className="card-heading">
                  <div>
                    <p className="eyebrow">Clients</p>
                    <h2 id="ov-clients-title">{d ? plural(d.clients.total, "client") : "Clients"}</h2>
                  </div>
                  <button className="text-button" onClick={() => onNavigate("clients")}>
                    View all <ArrowRight size={14} />
                  </button>
                </div>
                {d ? (
                  <div className="ov-clients">
                    <div className="ov-meter" aria-hidden="true">
                      <span style={{ width: `${d.clients.total ? (d.clients.subscribed / d.clients.total) * 100 : 0}%` }} />
                    </div>
                    <p>
                      <strong className="num">{d.clients.subscribed}</strong> of {d.clients.total} on an active
                      subscription
                    </p>
                  </div>
                ) : (
                  <SkeletonRows rows={1} label="Loading clients" />
                )}
                <ClientShare load={clientList} />
              </article>
            </div>
          </section>

          <section className="card card--pad ov-recent" aria-labelledby="ov-recent-title">
            <div className="card-heading">
              <div>
                <p className="eyebrow">Latest</p>
                <h2 id="ov-recent-title">Recent commissions</h2>
              </div>
              <button className="text-button" onClick={() => onNavigate("commissions")}>
                All commissions <ArrowRight size={14} />
              </button>
            </div>
            {!d ? (
              <SkeletonRows rows={3} label="Loading commissions" />
            ) : d.recent_commissions.length === 0 ? (
              <EmptyState icon={Receipt} title="Nothing earned yet">
                Commission appears here each time a client you referred pays their subscription.
              </EmptyState>
            ) : (
              <ul className="ov-commissions">
                {d.recent_commissions.map((c) => (
                  <RecentRow key={c.id} c={c} onOpen={() => setOpen(c)} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {open && <CommissionSheet c={open} onClose={() => setOpen(null)} />}
    </>
  );
}

/**
 * This month, this FY and lifetime, in paise. The dashboard's twelve months
 * always cover the current financial year (April to March), so the FY total
 * needs no extra call.
 */
function commissionFigures(d: Dashboard) {
  const today = todayIst();
  const [y, m] = today.split("-").map(Number);
  const fyStart = m >= 4 ? y : y - 1;
  const months = d.earnings_by_month;
  const at = (month: string) => toPaise(months.find((x) => x.month === month)?.amount);
  const prevMonth = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
  const fy = months.filter((x) => x.month >= `${fyStart}-04`).reduce((s, x) => s + toPaise(x.amount), 0);
  return {
    month: at(today.slice(0, 7)),
    prev: at(prevMonth),
    prevLabel: fmtMonth(prevMonth),
    fy,
    fyLabel: `FY ${fyStart}–${String(fyStart + 1).slice(2)}`,
    /** Months of this FY so far, the current one included: April = 1, March = 12. */
    fyMonths: ((m + 8) % 12) + 1,
    lifetime: toPaise(d.wallet.lifetime),
    onHold: toPaise(d.wallet.pending),
    inProgress: toPaise(d.wallet.in_progress),
  };
}

function CommissionSummary({
  d,
  me,
  contactEmail,
  onNavigate,
}: {
  d: Dashboard | null;
  me: PartnerMe;
  contactEmail: string;
  onNavigate: (view: View) => void;
}) {
  const f = d ? commissionFigures(d) : null;
  const figure = (label: string, paise: number | undefined, sub: React.ReactNode) => (
    <div>
      <dt>{label}</dt>
      <dd>
        {f ? (
          <>
            <strong className="num">{inr(fromPaise(paise ?? 0))}</strong>
            <small>{sub}</small>
          </>
        ) : (
          <span className="skel skel--lg" />
        )}
      </dd>
    </div>
  );

  return (
    <section className="card card--pad ov-comm" aria-labelledby="ov-comm-title">
      <div className="card-heading">
        <div>
          <p className="eyebrow">Your commission</p>
          <h2 id="ov-comm-title">
            {me.commission_rate ? (
              <>
                <span className="num">{rateLabel(me.commission_rate)}</span> of each client&apos;s subscription fee
              </>
            ) : (
              "Your commission rate hasn't been set yet"
            )}
          </h2>
          <p className="ov-comm__terms">
            {me.commission_rate ? (
              `Excluding GST · held ${me.hold_days} days, then withdrawable`
            ) : (
              <>
                Wealthoak will confirm it with you. Questions: <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
              </>
            )}
          </p>
        </div>
        <button className="text-button" onClick={() => onNavigate("commissions")}>
          All commissions <ArrowRight size={14} />
        </button>
      </div>

      <dl className="ov-comm__figures">
        {/* Last month in full rather than a % change: early in a month any change reads as a fall. */}
        {figure(
          "This month",
          f?.month,
          f && (f.prev > 0 ? <>{inrWhole(fromPaise(f.prev))} in {f.prevLabel}</> : `None in ${f.prevLabel}`),
        )}
        {figure(
          f?.fyLabel ?? "This FY",
          f?.fy,
          f && (f.fy > 0 ? <>{inrWhole(fromPaise(Math.round(f.fy / f.fyMonths)))} a month on average</> : "None so far"),
        )}
        {figure(
          "Lifetime",
          f?.lifetime,
          f &&
            (f.inProgress > 0 ? (
              <>{inrWhole(fromPaise(f.inProgress))} more in progress</>
            ) : f.onHold > 0 ? (
              <>{inrWhole(fromPaise(f.onHold))} still on hold</>
            ) : (
              "Nothing on hold"
            )),
        )}
      </dl>
      <p className="ov-comm__note">
        {me.commission_program
          ? "Includes commission still on hold. Commission in progress and reversed commission are left out."
          : "Includes commission still on hold. Reversed commission is left out."}
      </p>
    </section>
  );
}

const SHARE_TOP = 5;

/** The clients who earned the partner the most, with each one's share of the total. */
function ClientShare({ load }: { load: Load<{ items: ClientSummary[] }> }) {
  const earning = (load.data?.items ?? [])
    .map((c) => ({ id: c.referral_id, name: c.client_name || "Client", paise: toPaise(c.commission_total) }))
    .filter((c) => c.paise > 0)
    .sort((a, b) => b.paise - a.paise);
  const total = earning.reduce((s, c) => s + c.paise, 0);
  const top = earning.slice(0, SHARE_TOP);
  const rest = earning.slice(SHARE_TOP);

  return (
    <div className="ov-share">
      <p className="ov-share__title">Commission by client</p>
      {load.error && !load.data ? (
        <LoadError message={load.error} onRetry={load.reload} />
      ) : !load.data ? (
        <SkeletonRows rows={3} label="Loading commission by client" />
      ) : earning.length === 0 ? (
        <p className="ov-share__empty">No commission from clients yet.</p>
      ) : (
        <ul>
          {top.map((c) => {
            const share = (c.paise * 100) / total;
            return (
              <li key={c.id}>
                <div>
                  <span>{c.name}</span>
                  <span className="num">
                    <strong>{inrWhole(fromPaise(c.paise))}</strong>{" "}
                    <small>{Math.round(share)}%</small>
                  </span>
                </div>
                <div className="ov-share__bar" aria-hidden="true">
                  <span style={{ width: `${share}%` }} />
                </div>
              </li>
            );
          })}
          {rest.length > 0 && (
            <li className="ov-share__rest">
              {plural(rest.length, "more client")} · {inrWhole(fromPaise(rest.reduce((s, c) => s + c.paise, 0)))}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function WalletTiles({ d, onNavigate }: { d: Dashboard | null; onNavigate: (view: View) => void }) {
  const w = d?.wallet;
  const tile = (label: string, value: string | undefined, sub: React.ReactNode, tone = "") => (
    <article className="card stat">
      <p className="eyebrow">{label}</p>
      {w ? <strong className={tone}>{inr(value)}</strong> : <span className="skel skel--lg" style={{ marginTop: 10, width: "70%" }} />}
      <small>{w ? sub : " "}</small>
    </article>
  );
  return (
    <section className="stats" aria-label="Wallet">
      {tile(
        "Available",
        w?.available,
        <button className="link-button" onClick={() => onNavigate("wallet")}>
          Withdraw to your bank
        </button>,
        "is-green",
      )}
      {tile(
        "Pending",
        w?.pending,
        w?.next_release ? (
          <>
            {inr(w.next_release.amount)} releases on {fmtDate(w.next_release.date)}
          </>
        ) : (
          `Held ${w?.hold_days ?? 30} days after each payment`
        ),
      )}
      {tile("In process", w?.in_process, "Withdrawals being paid out")}
      {tile("Withdrawn", w?.withdrawn, "Paid to your bank, lifetime")}
    </section>
  );
}

const STEPS: { key: "submitted" | "contacted" | "converted" | "rejected"; label: string; hint: string }[] = [
  { key: "submitted", label: "Submitted", hint: "Waiting for Wealthoak to reach out" },
  { key: "contacted", label: "Contacted", hint: "In conversation with Wealthoak" },
  { key: "converted", label: "Converted", hint: "Now a Wealthoak client" },
  { key: "rejected", label: "Not taken forward", hint: "See the reason under Referrals" },
];

function Funnel({ counts }: { counts: Dashboard["referrals"] }) {
  const max = Math.max(1, counts.total);
  return (
    <ul className="ov-funnel">
      {STEPS.map((s) => (
        <li key={s.key} className={`is-${s.key}`}>
          <div>
            <span>{s.label}</span>
            <strong className="num">{counts[s.key]}</strong>
          </div>
          <div className="ov-funnel__bar" aria-hidden="true">
            <span style={{ width: `${(counts[s.key] / max) * 100}%` }} />
          </div>
          <small>{s.hint}</small>
        </li>
      ))}
    </ul>
  );
}

function RecentRow({ c, onOpen }: { c: Commission; onOpen: () => void }) {
  const st = COMMISSION_STATE[c.state];
  return (
    <li className="client-row" onClick={onOpen}>
      <span className="ov-commissions__who">
        <button type="button" className="client-open" onClick={onOpen} aria-haspopup="dialog">
          {c.client_name ?? "Client"}
        </button>
        <small>
          {c.plan_name ? `${c.plan_name} · ` : ""}
          {fmtDate(c.earned_at)}
          {c.invoice_number ? ` · ${c.invoice_number}` : ""}
        </small>
      </span>
      <span className="ov-commissions__state">
        <Status tone={st.tone}>{st.label}</Status>
        <small>
          {c.state === "in_progress"
            ? "not payable yet"
            : c.state === "pending"
              ? `until ${fmtDate(c.available_at)}`
              : c.state === "reversed"
                ? c.reversed_reason ?? ""
                : `since ${fmtDate(c.available_at)}`}
        </small>
      </span>
      <strong className={`ov-commissions__amt num${c.state === "reversed" ? " is-struck" : ""}`}>{inr(c.amount)}</strong>
    </li>
  );
}
